// Translations for chrome that lives outside the main LocaleBundle:
// navbar extras, footer, pricing table, trust badges, videos pages, 404.

import type { Lang } from "./types";
import { detectLang } from "./detect";

export interface ExtraBundle {
  nav: { videos: string; support: string };
  appBadges: {
    downloadOn: string;
    appStore: string;
    getItOn: string;
    googlePlay: string;
    iosApp: string;
    androidApp: string;
    appStoreAria: string;
    googlePlayAria: string;
    takeWithYou: string;
  };
  footer: {
    tagline: string;
    copyright: string;
    tagBuiltFor: string;
    cols: { product: string; company: string; resources: string; legal: string };
    links: {
      features: string;
      pricing: string;
      integrations: string;
      industries: string;
      about: string;
      customers: string;
      contact: string;
      support: string;
      helpCenter: string;
      apiDocs: string;
      privacy: string;
      terms: string;
    };
  };
  pricing: {
    eyebrow: string;
    title: string;
    subtitle: string;
    mostPopular: string;
    perUserMo: string;
    tiers: { name: string; price: string; blurb: string; cta: string }[];
    f: {
      storage: string;
      contacts: string;
      deals: string;
      cases: string;
      calendar: string;
      reports: string;
      apps: string;
      campaigns: string;
      email: string;
      googleSync: string;
      quickbooks: string;
      sms: string;
      voice: string;
      creditCard: string;
      support: string;
      noSupport: string;
      api: string;
    };
  };
  trust: {
    eyebrow: string;
    title: string;
    subtitle: string;
    badges: { label: string; short: string; tooltip: string }[];
  };
  videos: {
    metaTitle: string;
    metaDescription: string;
    pageTitle: string;
    eyebrow: string;
    heading: string;
    subtitle: string;
    allVideos: string;
    about: string;
    more: string;
    unsupported: string;
    thumbnailAlt: string;
    secUnit: string;
    items: Record<string, { name: string; description: string }>;
  };
  notFound: {
    metaTitle: string;
    metaDescription: string;
    heading: string;
    home: string;
  };
}

const en: ExtraBundle = {
  nav: { videos: "Videos", support: "Support" },
  appBadges: {
    downloadOn: "Download on the",
    appStore: "App Store",
    getItOn: "Get it on",
    googlePlay: "Google Play",
    iosApp: "iOS App",
    androidApp: "Android App",
    appStoreAria: "Download FreeCRM on the App Store",
    googlePlayAria: "Get FreeCRM on Google Play",
    takeWithYou: "Take FreeCRM with you",
  },
  footer: {
    tagline: "The free CRM that grows with your business.",
    copyright: "All rights reserved.",
    tagBuiltFor: "Built for teams who deserve better than HubSpot.",
    cols: { product: "Product", company: "Company", resources: "Resources", legal: "Legal" },
    links: {
      features: "Features", pricing: "Pricing", integrations: "Integrations", industries: "Industries",
      about: "About", customers: "Customers", contact: "Contact", support: "Support",
      helpCenter: "Help center", apiDocs: "API docs", privacy: "Privacy", terms: "Terms",
    },
  },
  pricing: {
    eyebrow: "Pricing",
    title: "CRM software pricing",
    subtitle: "US phone numbers only $1.15/month · $0.01 per minute · $0.01 per SMS",
    mostPopular: "Most popular",
    perUserMo: "/user/mo",
    tiers: [
      { name: "Free", price: "$0", blurb: "Free forever — the essentials to manage contacts, deals, and tasks.", cta: "Sign up free" },
      { name: "Pro", price: "$10", blurb: "Full CRM power for growing sales teams.", cta: "Sign up" },
      { name: "Enterprise", price: "$25", blurb: "Advanced features for high-volume teams.", cta: "Sign up" },
    ],
    f: {
      storage: "Unlimited storage", contacts: "Contacts & companies", deals: "Deals & tasks",
      cases: "Cases & calls", calendar: "Calendar & docs", reports: "Reports",
      apps: "iOS & Android apps", campaigns: "Campaigns & forms", email: "Email",
      googleSync: "Google Sync", quickbooks: "QuickBooks", sms: "SMS", voice: "Voice calling",
      creditCard: "Credit card", support: "Customer Support", noSupport: "No Support", api: "API access",
    },
  },
  trust: {
    eyebrow: "Verified claims",
    title: "Security you can verify, not just adjectives",
    subtitle: "Hover any badge to see exactly what it means. No “military-grade” marketing — just the specific standards we’re held to.",
    badges: [
      { label: "TLS 1.3 in transit", short: "Encrypted in transit", tooltip: "Every connection to FreeCRM is encrypted with TLS 1.3 — the latest transport layer security standard — so data exchanged between your browser and our servers cannot be read or tampered with in flight." },
      { label: "AES-256 at rest", short: "Encrypted at rest", tooltip: "All customer data stored in our databases and backups is encrypted at rest using AES-256, the same symmetric cipher used to protect classified information in regulated industries." },
      { label: "SOC 2 Type II", short: "Independently audited", tooltip: "Our infrastructure is hosted in SOC 2 Type II audited data centers. An independent auditor verifies, on an ongoing basis, that controls for security, availability, and confidentiality operate effectively over time." },
      { label: "GDPR & CCPA ready", short: "Privacy-first by default", tooltip: "FreeCRM supports GDPR and CCPA requirements with a signed DPA, data export and deletion tools, regional data residency on Enterprise, and granular role-based access controls." },
    ],
  },
  videos: {
    metaTitle: "FreeCRM video tutorials",
    metaDescription: "Short FreeCRM video tutorials covering setup, custom fields, calendar integration, and the basics of using FreeCRM.",
    pageTitle: "Video tutorials",
    eyebrow: "Video library",
    heading: "FreeCRM video tutorials",
    subtitle: "Short, focused walkthroughs to help you set up FreeCRM, customize forms, sync your calendar, and onboard your team.",
    allVideos: "All videos",
    about: "About this video",
    more: "More tutorials",
    unsupported: "Your browser does not support the video tag.",
    thumbnailAlt: "video thumbnail",
    secUnit: "sec",
    items: {
      "free-crm-user-guide": { name: "FreeCRM user guide", description: "A walkthrough of the FreeCRM interface: navigating contacts, deals, tasks, and the activity stream so new users can get productive in minutes." },
      "company-profile-and-user-setup-guide": { name: "Company profile and user setup guide", description: "How to configure your FreeCRM company profile, invite teammates, and assign roles so your sales team is ready to start working leads." },
      "adding-custom-fields-to-forms": { name: "Adding custom fields to forms", description: "Step-by-step guide to adding custom fields to FreeCRM web forms so you can capture exactly the lead data your sales process needs." },
      "adding-and-customizing-crm-form-fields": { name: "Adding and customizing CRM form fields", description: "Learn how to add, reorder, and customize CRM form fields in FreeCRM to match your team's qualification criteria and pipeline stages." },
      "android-crm-app-user-guide": { name: "Android CRM app user guide", description: "A walkthrough of the FreeCRM Android app: navigating contacts, deals, and tasks on mobile so reps can stay productive on the go." },
      "managing-and-editing-contacts": { name: "Managing and editing contacts", description: "How to create, update, and organize contacts in FreeCRM so your customer records stay clean, current, and easy for the team to action." },
      "crm-calendar-integration-guide": { name: "CRM calendar integration guide", description: "How to connect Google Calendar to FreeCRM so meetings, follow-ups, and scheduled calls stay in sync between your CRM and your calendar." },
    },
  },
  notFound: {
    metaTitle: "Page Not Found — FreeCRM",
    metaDescription: "The page you're looking for doesn't exist. Return to FreeCRM to find what you need.",
    heading: "Oops! Page not found",
    home: "Return to Home",
  },
};

const es: ExtraBundle = {
  nav: { videos: "Vídeos", support: "Soporte" },
  appBadges: {
    downloadOn: "Descárgala en", appStore: "App Store", getItOn: "Disponible en", googlePlay: "Google Play",
    iosApp: "App para iOS", androidApp: "App para Android",
    appStoreAria: "Descarga FreeCRM en el App Store", googlePlayAria: "Consigue FreeCRM en Google Play",
    takeWithYou: "Lleva FreeCRM contigo",
  },
  footer: {
    tagline: "El CRM gratuito que crece con tu negocio.",
    copyright: "Todos los derechos reservados.",
    tagBuiltFor: "Creado para equipos que merecen algo mejor que HubSpot.",
    cols: { product: "Producto", company: "Empresa", resources: "Recursos", legal: "Legal" },
    links: {
      features: "Funciones", pricing: "Precios", integrations: "Integraciones", industries: "Sectores",
      about: "Acerca de", customers: "Clientes", contact: "Contacto", support: "Soporte",
      helpCenter: "Centro de ayuda", apiDocs: "Documentación de la API", privacy: "Privacidad", terms: "Términos",
    },
  },
  pricing: {
    eyebrow: "Precios",
    title: "Precios del software CRM",
    subtitle: "Números de teléfono de EE. UU. por solo $1.15/mes · $0.01 por minuto · $0.01 por SMS",
    mostPopular: "Más popular",
    perUserMo: "/usuario/mes",
    tiers: [
      { name: "Free", price: "$0", blurb: "Gratis para siempre: lo esencial para gestionar contactos, negocios y tareas.", cta: "Regístrate gratis" },
      { name: "Pro", price: "$10", blurb: "Todo el poder del CRM para equipos de ventas en crecimiento.", cta: "Registrarse" },
      { name: "Enterprise", price: "$25", blurb: "Funciones avanzadas para equipos de alto volumen.", cta: "Registrarse" },
    ],
    f: {
      storage: "Almacenamiento ilimitado", contacts: "Contactos y empresas", deals: "Negocios y tareas",
      cases: "Casos y llamadas", calendar: "Calendario y documentos", reports: "Informes",
      apps: "Apps para iOS y Android", campaigns: "Campañas y formularios", email: "Correo electrónico",
      googleSync: "Sincronización con Google", quickbooks: "QuickBooks", sms: "SMS", voice: "Llamadas de voz",
      creditCard: "Tarjeta de crédito", support: "Atención al cliente", noSupport: "Sin soporte", api: "Acceso a la API",
    },
  },
  trust: {
    eyebrow: "Datos verificados",
    title: "Seguridad que puedes verificar, no solo adjetivos",
    subtitle: "Pasa el cursor sobre cualquier distintivo para ver qué significa exactamente. Sin marketing de “grado militar”: solo los estándares concretos que cumplimos.",
    badges: [
      { label: "TLS 1.3 en tránsito", short: "Cifrado en tránsito", tooltip: "Cada conexión con FreeCRM está cifrada con TLS 1.3, el estándar más reciente de seguridad de la capa de transporte, de modo que los datos intercambiados entre tu navegador y nuestros servidores no pueden leerse ni alterarse en tránsito." },
      { label: "AES-256 en reposo", short: "Cifrado en reposo", tooltip: "Todos los datos de clientes almacenados en nuestras bases de datos y copias de seguridad se cifran en reposo con AES-256, el mismo cifrado simétrico usado para proteger información clasificada en sectores regulados." },
      { label: "SOC 2 Tipo II", short: "Auditado de forma independiente", tooltip: "Nuestra infraestructura está alojada en centros de datos auditados bajo SOC 2 Tipo II. Un auditor independiente verifica de forma continua que los controles de seguridad, disponibilidad y confidencialidad funcionan de manera efectiva." },
      { label: "Listo para RGPD y CCPA", short: "Privacidad por defecto", tooltip: "FreeCRM cumple los requisitos del RGPD y la CCPA con un DPA firmado, herramientas de exportación y eliminación de datos, residencia regional de datos en Enterprise y controles de acceso granulares por rol." },
    ],
  },
  videos: {
    metaTitle: "Tutoriales en vídeo de FreeCRM",
    metaDescription: "Tutoriales breves en vídeo sobre configuración, campos personalizados, integración de calendario y los fundamentos de FreeCRM.",
    pageTitle: "Tutoriales en vídeo",
    eyebrow: "Videoteca",
    heading: "Tutoriales en vídeo de FreeCRM",
    subtitle: "Guías breves y prácticas para configurar FreeCRM, personalizar formularios, sincronizar tu calendario e incorporar a tu equipo.",
    allVideos: "Todos los vídeos",
    about: "Sobre este vídeo",
    more: "Más tutoriales",
    unsupported: "Tu navegador no admite la etiqueta de vídeo.",
    thumbnailAlt: "miniatura del vídeo",
    secUnit: "s",
    items: {
      "free-crm-user-guide": { name: "Guía de usuario de FreeCRM", description: "Un recorrido por la interfaz de FreeCRM: navegar por contactos, negocios, tareas y el historial de actividad para que los nuevos usuarios sean productivos en minutos." },
      "company-profile-and-user-setup-guide": { name: "Guía de perfil de empresa y configuración de usuarios", description: "Cómo configurar el perfil de tu empresa en FreeCRM, invitar a compañeros y asignar roles para que tu equipo de ventas empiece a trabajar los leads." },
      "adding-custom-fields-to-forms": { name: "Añadir campos personalizados a los formularios", description: "Guía paso a paso para añadir campos personalizados a los formularios web de FreeCRM y capturar exactamente los datos que necesita tu proceso de ventas." },
      "adding-and-customizing-crm-form-fields": { name: "Añadir y personalizar campos de formulario del CRM", description: "Aprende a añadir, reordenar y personalizar los campos de formulario del CRM en FreeCRM según tus criterios de calificación y etapas del pipeline." },
      "android-crm-app-user-guide": { name: "Guía de la app CRM para Android", description: "Un recorrido por la app de FreeCRM para Android: contactos, negocios y tareas desde el móvil para que tu equipo siga siendo productivo en cualquier lugar." },
      "managing-and-editing-contacts": { name: "Gestionar y editar contactos", description: "Cómo crear, actualizar y organizar contactos en FreeCRM para que tus registros de clientes estén siempre limpios, actualizados y listos para actuar." },
      "crm-calendar-integration-guide": { name: "Guía de integración del calendario con el CRM", description: "Cómo conectar Google Calendar con FreeCRM para que reuniones, seguimientos y llamadas programadas estén sincronizados entre tu CRM y tu calendario." },
    },
  },
  notFound: {
    metaTitle: "Página no encontrada — FreeCRM",
    metaDescription: "La página que buscas no existe. Vuelve a FreeCRM para encontrar lo que necesitas.",
    heading: "¡Vaya! Página no encontrada",
    home: "Volver al inicio",
  },
};

const fr: ExtraBundle = {
  nav: { videos: "Vidéos", support: "Assistance" },
  appBadges: {
    downloadOn: "Télécharger dans l’", appStore: "App Store", getItOn: "Disponible sur", googlePlay: "Google Play",
    iosApp: "App iOS", androidApp: "App Android",
    appStoreAria: "Télécharger FreeCRM sur l’App Store", googlePlayAria: "Obtenir FreeCRM sur Google Play",
    takeWithYou: "Emportez FreeCRM partout",
  },
  footer: {
    tagline: "Le CRM gratuit qui grandit avec votre entreprise.",
    copyright: "Tous droits réservés.",
    tagBuiltFor: "Conçu pour les équipes qui méritent mieux que HubSpot.",
    cols: { product: "Produit", company: "Entreprise", resources: "Ressources", legal: "Mentions légales" },
    links: {
      features: "Fonctionnalités", pricing: "Tarifs", integrations: "Intégrations", industries: "Secteurs",
      about: "À propos", customers: "Clients", contact: "Contact", support: "Assistance",
      helpCenter: "Centre d’aide", apiDocs: "Documentation API", privacy: "Confidentialité", terms: "Conditions",
    },
  },
  pricing: {
    eyebrow: "Tarifs",
    title: "Tarifs du logiciel CRM",
    subtitle: "Numéros américains à seulement 1,15 $/mois · 0,01 $ la minute · 0,01 $ par SMS",
    mostPopular: "Le plus populaire",
    perUserMo: "/utilisateur/mois",
    tiers: [
      { name: "Free", price: "$0", blurb: "Gratuit à vie — l’essentiel pour gérer contacts, affaires et tâches.", cta: "Inscription gratuite" },
      { name: "Pro", price: "$10", blurb: "Toute la puissance du CRM pour les équipes commerciales en croissance.", cta: "S’inscrire" },
      { name: "Enterprise", price: "$25", blurb: "Fonctionnalités avancées pour les équipes à fort volume.", cta: "S’inscrire" },
    ],
    f: {
      storage: "Stockage illimité", contacts: "Contacts et entreprises", deals: "Affaires et tâches",
      cases: "Dossiers et appels", calendar: "Agenda et documents", reports: "Rapports",
      apps: "Applications iOS et Android", campaigns: "Campagnes et formulaires", email: "E-mail",
      googleSync: "Synchronisation Google", quickbooks: "QuickBooks", sms: "SMS", voice: "Appels vocaux",
      creditCard: "Carte bancaire", support: "Assistance client", noSupport: "Sans assistance", api: "Accès API",
    },
  },
  trust: {
    eyebrow: "Engagements vérifiés",
    title: "Une sécurité vérifiable, pas de simples adjectifs",
    subtitle: "Survolez un badge pour voir exactement ce qu’il signifie. Pas de marketing « de qualité militaire » — uniquement les normes précises que nous respectons.",
    badges: [
      { label: "TLS 1.3 en transit", short: "Chiffré en transit", tooltip: "Chaque connexion à FreeCRM est chiffrée en TLS 1.3 — la dernière norme de sécurité de la couche transport — afin que les données échangées entre votre navigateur et nos serveurs ne puissent être lues ni altérées en transit." },
      { label: "AES-256 au repos", short: "Chiffré au repos", tooltip: "Toutes les données clients stockées dans nos bases et sauvegardes sont chiffrées au repos en AES-256, le chiffrement symétrique utilisé pour protéger les informations classifiées dans les secteurs réglementés." },
      { label: "SOC 2 Type II", short: "Audité indépendamment", tooltip: "Notre infrastructure est hébergée dans des centres de données audités SOC 2 Type II. Un auditeur indépendant vérifie en continu que les contrôles de sécurité, de disponibilité et de confidentialité fonctionnent efficacement." },
      { label: "Conforme RGPD et CCPA", short: "Confidentialité par défaut", tooltip: "FreeCRM répond aux exigences du RGPD et du CCPA avec un DPA signé, des outils d’export et de suppression des données, une résidence régionale des données en Enterprise et des contrôles d’accès granulaires par rôle." },
    ],
  },
  videos: {
    metaTitle: "Tutoriels vidéo FreeCRM",
    metaDescription: "Courts tutoriels vidéo FreeCRM sur la configuration, les champs personnalisés, l’intégration de l’agenda et les bases de FreeCRM.",
    pageTitle: "Tutoriels vidéo",
    eyebrow: "Vidéothèque",
    heading: "Tutoriels vidéo FreeCRM",
    subtitle: "Des démonstrations courtes et ciblées pour configurer FreeCRM, personnaliser vos formulaires, synchroniser votre agenda et former votre équipe.",
    allVideos: "Toutes les vidéos",
    about: "À propos de cette vidéo",
    more: "Plus de tutoriels",
    unsupported: "Votre navigateur ne prend pas en charge la balise vidéo.",
    thumbnailAlt: "miniature de la vidéo",
    secUnit: "s",
    items: {
      "free-crm-user-guide": { name: "Guide d’utilisation de FreeCRM", description: "Une visite de l’interface FreeCRM : navigation dans les contacts, les affaires, les tâches et le fil d’activité pour être productif en quelques minutes." },
      "company-profile-and-user-setup-guide": { name: "Guide du profil d’entreprise et de la configuration des utilisateurs", description: "Comment configurer le profil de votre entreprise dans FreeCRM, inviter vos collègues et attribuer des rôles pour que votre équipe commerciale démarre." },
      "adding-custom-fields-to-forms": { name: "Ajouter des champs personnalisés aux formulaires", description: "Guide pas à pas pour ajouter des champs personnalisés aux formulaires web FreeCRM et collecter exactement les données dont votre processus de vente a besoin." },
      "adding-and-customizing-crm-form-fields": { name: "Ajouter et personnaliser les champs de formulaire du CRM", description: "Apprenez à ajouter, réorganiser et personnaliser les champs de formulaire dans FreeCRM selon vos critères de qualification et vos étapes de pipeline." },
      "android-crm-app-user-guide": { name: "Guide de l’application CRM Android", description: "Une visite de l’application Android FreeCRM : contacts, affaires et tâches sur mobile pour rester productif en déplacement." },
      "managing-and-editing-contacts": { name: "Gérer et modifier les contacts", description: "Comment créer, mettre à jour et organiser vos contacts dans FreeCRM pour des fiches clients propres, à jour et exploitables par toute l’équipe." },
      "crm-calendar-integration-guide": { name: "Guide d’intégration de l’agenda au CRM", description: "Comment connecter Google Agenda à FreeCRM pour synchroniser réunions, relances et appels planifiés entre votre CRM et votre agenda." },
    },
  },
  notFound: {
    metaTitle: "Page introuvable — FreeCRM",
    metaDescription: "La page que vous cherchez n’existe pas. Revenez sur FreeCRM pour trouver ce dont vous avez besoin.",
    heading: "Oups ! Page introuvable",
    home: "Retour à l’accueil",
  },
};

const pt: ExtraBundle = {
  nav: { videos: "Vídeos", support: "Suporte" },
  appBadges: {
    downloadOn: "Baixe na", appStore: "App Store", getItOn: "Disponível no", googlePlay: "Google Play",
    iosApp: "App iOS", androidApp: "App Android",
    appStoreAria: "Baixe o FreeCRM na App Store", googlePlayAria: "Obtenha o FreeCRM no Google Play",
    takeWithYou: "Leve o FreeCRM com você",
  },
  footer: {
    tagline: "O CRM gratuito que cresce com o seu negócio.",
    copyright: "Todos os direitos reservados.",
    tagBuiltFor: "Feito para equipes que merecem algo melhor que o HubSpot.",
    cols: { product: "Produto", company: "Empresa", resources: "Recursos", legal: "Jurídico" },
    links: {
      features: "Funcionalidades", pricing: "Preços", integrations: "Integrações", industries: "Setores",
      about: "Sobre", customers: "Clientes", contact: "Contato", support: "Suporte",
      helpCenter: "Central de ajuda", apiDocs: "Documentação da API", privacy: "Privacidade", terms: "Termos",
    },
  },
  pricing: {
    eyebrow: "Preços",
    title: "Preços do software de CRM",
    subtitle: "Números de telefone dos EUA por apenas $1,15/mês · $0,01 por minuto · $0,01 por SMS",
    mostPopular: "Mais popular",
    perUserMo: "/usuário/mês",
    tiers: [
      { name: "Free", price: "$0", blurb: "Grátis para sempre — o essencial para gerenciar contatos, negócios e tarefas.", cta: "Cadastre-se grátis" },
      { name: "Pro", price: "$10", blurb: "Todo o poder do CRM para equipes de vendas em crescimento.", cta: "Cadastre-se" },
      { name: "Enterprise", price: "$25", blurb: "Recursos avançados para equipes de alto volume.", cta: "Cadastre-se" },
    ],
    f: {
      storage: "Armazenamento ilimitado", contacts: "Contatos e empresas", deals: "Negócios e tarefas",
      cases: "Casos e chamadas", calendar: "Agenda e documentos", reports: "Relatórios",
      apps: "Apps iOS e Android", campaigns: "Campanhas e formulários", email: "E-mail",
      googleSync: "Sincronização com Google", quickbooks: "QuickBooks", sms: "SMS", voice: "Chamadas de voz",
      creditCard: "Cartão de crédito", support: "Suporte ao cliente", noSupport: "Sem suporte", api: "Acesso à API",
    },
  },
  trust: {
    eyebrow: "Alegações verificadas",
    title: "Segurança que você pode verificar, não apenas adjetivos",
    subtitle: "Passe o mouse sobre qualquer selo para ver exatamente o que ele significa. Sem marketing de “nível militar” — apenas os padrões específicos que seguimos.",
    badges: [
      { label: "TLS 1.3 em trânsito", short: "Criptografado em trânsito", tooltip: "Toda conexão com o FreeCRM é criptografada com TLS 1.3 — o padrão mais recente de segurança da camada de transporte — para que os dados trocados entre o seu navegador e os nossos servidores não possam ser lidos nem alterados em trânsito." },
      { label: "AES-256 em repouso", short: "Criptografado em repouso", tooltip: "Todos os dados de clientes armazenados em nossos bancos de dados e backups são criptografados em repouso com AES-256, a mesma cifra simétrica usada para proteger informações confidenciais em setores regulados." },
      { label: "SOC 2 Tipo II", short: "Auditado de forma independente", tooltip: "Nossa infraestrutura fica em data centers auditados sob SOC 2 Tipo II. Um auditor independente verifica continuamente que os controles de segurança, disponibilidade e confidencialidade funcionam de forma eficaz." },
      { label: "Pronto para LGPD/GDPR e CCPA", short: "Privacidade por padrão", tooltip: "O FreeCRM atende aos requisitos de GDPR e CCPA com DPA assinado, ferramentas de exportação e exclusão de dados, residência regional de dados no plano Enterprise e controles de acesso granulares por função." },
    ],
  },
  videos: {
    metaTitle: "Tutoriais em vídeo do FreeCRM",
    metaDescription: "Tutoriais curtos em vídeo sobre configuração, campos personalizados, integração de agenda e o básico do FreeCRM.",
    pageTitle: "Tutoriais em vídeo",
    eyebrow: "Videoteca",
    heading: "Tutoriais em vídeo do FreeCRM",
    subtitle: "Demonstrações curtas e objetivas para configurar o FreeCRM, personalizar formulários, sincronizar sua agenda e treinar sua equipe.",
    allVideos: "Todos os vídeos",
    about: "Sobre este vídeo",
    more: "Mais tutoriais",
    unsupported: "Seu navegador não suporta a tag de vídeo.",
    thumbnailAlt: "miniatura do vídeo",
    secUnit: "s",
    items: {
      "free-crm-user-guide": { name: "Guia do usuário do FreeCRM", description: "Um passeio pela interface do FreeCRM: navegar por contatos, negócios, tarefas e o histórico de atividades para novos usuários produzirem em minutos." },
      "company-profile-and-user-setup-guide": { name: "Guia de perfil da empresa e configuração de usuários", description: "Como configurar o perfil da sua empresa no FreeCRM, convidar colegas e atribuir funções para a equipe de vendas começar a trabalhar os leads." },
      "adding-custom-fields-to-forms": { name: "Adicionando campos personalizados aos formulários", description: "Guia passo a passo para adicionar campos personalizados aos formulários web do FreeCRM e capturar exatamente os dados que seu processo de vendas exige." },
      "adding-and-customizing-crm-form-fields": { name: "Adicionando e personalizando campos de formulário do CRM", description: "Aprenda a adicionar, reordenar e personalizar campos de formulário no FreeCRM conforme seus critérios de qualificação e etapas do funil." },
      "android-crm-app-user-guide": { name: "Guia do app CRM para Android", description: "Um passeio pelo app Android do FreeCRM: contatos, negócios e tarefas no celular para a equipe produzir em qualquer lugar." },
      "managing-and-editing-contacts": { name: "Gerenciando e editando contatos", description: "Como criar, atualizar e organizar contatos no FreeCRM para manter os registros de clientes limpos, atualizados e fáceis de usar pela equipe." },
      "crm-calendar-integration-guide": { name: "Guia de integração de agenda com o CRM", description: "Como conectar o Google Agenda ao FreeCRM para manter reuniões, follow-ups e chamadas agendadas sincronizados entre o CRM e sua agenda." },
    },
  },
  notFound: {
    metaTitle: "Página não encontrada — FreeCRM",
    metaDescription: "A página que você procura não existe. Volte ao FreeCRM para encontrar o que precisa.",
    heading: "Ops! Página não encontrada",
    home: "Voltar ao início",
  },
};

const it: ExtraBundle = {
  nav: { videos: "Video", support: "Assistenza" },
  appBadges: {
    downloadOn: "Scarica su", appStore: "App Store", getItOn: "Disponibile su", googlePlay: "Google Play",
    iosApp: "App iOS", androidApp: "App Android",
    appStoreAria: "Scarica FreeCRM su App Store", googlePlayAria: "Ottieni FreeCRM su Google Play",
    takeWithYou: "Porta FreeCRM con te",
  },
  footer: {
    tagline: "Il CRM gratuito che cresce con la tua azienda.",
    copyright: "Tutti i diritti riservati.",
    tagBuiltFor: "Creato per team che meritano di meglio di HubSpot.",
    cols: { product: "Prodotto", company: "Azienda", resources: "Risorse", legal: "Legale" },
    links: {
      features: "Funzionalità", pricing: "Prezzi", integrations: "Integrazioni", industries: "Settori",
      about: "Chi siamo", customers: "Clienti", contact: "Contatti", support: "Assistenza",
      helpCenter: "Centro assistenza", apiDocs: "Documentazione API", privacy: "Privacy", terms: "Termini",
    },
  },
  pricing: {
    eyebrow: "Prezzi",
    title: "Prezzi del software CRM",
    subtitle: "Numeri di telefono USA a soli $1,15/mese · $0,01 al minuto · $0,01 per SMS",
    mostPopular: "Più popolare",
    perUserMo: "/utente/mese",
    tiers: [
      { name: "Free", price: "$0", blurb: "Gratis per sempre — l’essenziale per gestire contatti, trattative e attività.", cta: "Registrati gratis" },
      { name: "Pro", price: "$10", blurb: "Tutta la potenza del CRM per team di vendita in crescita.", cta: "Registrati" },
      { name: "Enterprise", price: "$25", blurb: "Funzionalità avanzate per team ad alto volume.", cta: "Registrati" },
    ],
    f: {
      storage: "Archiviazione illimitata", contacts: "Contatti e aziende", deals: "Trattative e attività",
      cases: "Casi e chiamate", calendar: "Calendario e documenti", reports: "Report",
      apps: "App iOS e Android", campaigns: "Campagne e moduli", email: "Email",
      googleSync: "Sincronizzazione Google", quickbooks: "QuickBooks", sms: "SMS", voice: "Chiamate vocali",
      creditCard: "Carta di credito", support: "Assistenza clienti", noSupport: "Nessuna assistenza", api: "Accesso API",
    },
  },
  trust: {
    eyebrow: "Dichiarazioni verificate",
    title: "Sicurezza verificabile, non solo aggettivi",
    subtitle: "Passa il mouse su un badge per vedere cosa significa esattamente. Niente marketing “di livello militare”: solo gli standard specifici che rispettiamo.",
    badges: [
      { label: "TLS 1.3 in transito", short: "Cifrato in transito", tooltip: "Ogni connessione a FreeCRM è cifrata con TLS 1.3 — il più recente standard di sicurezza del livello di trasporto — così i dati scambiati tra il tuo browser e i nostri server non possono essere letti né alterati in transito." },
      { label: "AES-256 a riposo", short: "Cifrato a riposo", tooltip: "Tutti i dati dei clienti archiviati nei nostri database e backup sono cifrati a riposo con AES-256, lo stesso cifrario simmetrico usato per proteggere informazioni riservate nei settori regolamentati." },
      { label: "SOC 2 Tipo II", short: "Verificato in modo indipendente", tooltip: "La nostra infrastruttura è ospitata in data center certificati SOC 2 Tipo II. Un revisore indipendente verifica su base continuativa che i controlli di sicurezza, disponibilità e riservatezza funzionino efficacemente." },
      { label: "Pronto per GDPR e CCPA", short: "Privacy by default", tooltip: "FreeCRM soddisfa i requisiti di GDPR e CCPA con DPA firmato, strumenti di esportazione e cancellazione dei dati, residenza regionale dei dati su Enterprise e controlli di accesso granulari basati sui ruoli." },
    ],
  },
  videos: {
    metaTitle: "Video tutorial di FreeCRM",
    metaDescription: "Brevi video tutorial su configurazione, campi personalizzati, integrazione del calendario e le basi di FreeCRM.",
    pageTitle: "Video tutorial",
    eyebrow: "Videoteca",
    heading: "Video tutorial di FreeCRM",
    subtitle: "Guide brevi e mirate per configurare FreeCRM, personalizzare i moduli, sincronizzare il calendario e formare il team.",
    allVideos: "Tutti i video",
    about: "Informazioni su questo video",
    more: "Altri tutorial",
    unsupported: "Il tuo browser non supporta il tag video.",
    thumbnailAlt: "anteprima del video",
    secUnit: "sec",
    items: {
      "free-crm-user-guide": { name: "Guida utente di FreeCRM", description: "Una panoramica dell’interfaccia di FreeCRM: contatti, trattative, attività e flusso delle attività per diventare produttivi in pochi minuti." },
      "company-profile-and-user-setup-guide": { name: "Guida al profilo aziendale e alla configurazione utenti", description: "Come configurare il profilo aziendale in FreeCRM, invitare i colleghi e assegnare i ruoli così che il team vendite possa iniziare a lavorare i lead." },
      "adding-custom-fields-to-forms": { name: "Aggiungere campi personalizzati ai moduli", description: "Guida passo passo per aggiungere campi personalizzati ai moduli web di FreeCRM e raccogliere esattamente i dati richiesti dal tuo processo di vendita." },
      "adding-and-customizing-crm-form-fields": { name: "Aggiungere e personalizzare i campi dei moduli CRM", description: "Scopri come aggiungere, riordinare e personalizzare i campi dei moduli in FreeCRM in base ai criteri di qualificazione e alle fasi della pipeline." },
      "android-crm-app-user-guide": { name: "Guida all’app CRM per Android", description: "Una panoramica dell’app Android di FreeCRM: contatti, trattative e attività da mobile per restare produttivi ovunque." },
      "managing-and-editing-contacts": { name: "Gestire e modificare i contatti", description: "Come creare, aggiornare e organizzare i contatti in FreeCRM per mantenere le anagrafiche clienti pulite, aggiornate e subito utilizzabili." },
      "crm-calendar-integration-guide": { name: "Guida all’integrazione del calendario con il CRM", description: "Come collegare Google Calendar a FreeCRM per mantenere sincronizzati riunioni, follow-up e chiamate programmate tra CRM e calendario." },
    },
  },
  notFound: {
    metaTitle: "Pagina non trovata — FreeCRM",
    metaDescription: "La pagina che cerchi non esiste. Torna su FreeCRM per trovare ciò che ti serve.",
    heading: "Ops! Pagina non trovata",
    home: "Torna alla home",
  },
};

const de: ExtraBundle = {
  nav: { videos: "Videos", support: "Support" },
  appBadges: {
    downloadOn: "Laden im", appStore: "App Store", getItOn: "Jetzt bei", googlePlay: "Google Play",
    iosApp: "iOS-App", androidApp: "Android-App",
    appStoreAria: "FreeCRM im App Store laden", googlePlayAria: "FreeCRM bei Google Play holen",
    takeWithYou: "Nehmen Sie FreeCRM mit",
  },
  footer: {
    tagline: "Das kostenlose CRM, das mit Ihrem Unternehmen wächst.",
    copyright: "Alle Rechte vorbehalten.",
    tagBuiltFor: "Gemacht für Teams, die Besseres verdienen als HubSpot.",
    cols: { product: "Produkt", company: "Unternehmen", resources: "Ressourcen", legal: "Rechtliches" },
    links: {
      features: "Funktionen", pricing: "Preise", integrations: "Integrationen", industries: "Branchen",
      about: "Über uns", customers: "Kunden", contact: "Kontakt", support: "Support",
      helpCenter: "Hilfecenter", apiDocs: "API-Dokumentation", privacy: "Datenschutz", terms: "AGB",
    },
  },
  pricing: {
    eyebrow: "Preise",
    title: "Preise für CRM-Software",
    subtitle: "US-Rufnummern für nur 1,15 $/Monat · 0,01 $ pro Minute · 0,01 $ pro SMS",
    mostPopular: "Am beliebtesten",
    perUserMo: "/Nutzer/Monat",
    tiers: [
      { name: "Free", price: "$0", blurb: "Für immer kostenlos — das Wesentliche für Kontakte, Deals und Aufgaben.", cta: "Kostenlos registrieren" },
      { name: "Pro", price: "$10", blurb: "Volle CRM-Power für wachsende Vertriebsteams.", cta: "Registrieren" },
      { name: "Enterprise", price: "$25", blurb: "Erweiterte Funktionen für Teams mit hohem Volumen.", cta: "Registrieren" },
    ],
    f: {
      storage: "Unbegrenzter Speicher", contacts: "Kontakte & Firmen", deals: "Deals & Aufgaben",
      cases: "Fälle & Anrufe", calendar: "Kalender & Dokumente", reports: "Berichte",
      apps: "iOS- & Android-Apps", campaigns: "Kampagnen & Formulare", email: "E-Mail",
      googleSync: "Google-Synchronisierung", quickbooks: "QuickBooks", sms: "SMS", voice: "Sprachanrufe",
      creditCard: "Kreditkarte", support: "Kundensupport", noSupport: "Kein Support", api: "API-Zugriff",
    },
  },
  trust: {
    eyebrow: "Geprüfte Angaben",
    title: "Sicherheit, die überprüfbar ist – keine bloßen Adjektive",
    subtitle: "Fahren Sie über ein Badge, um genau zu sehen, was es bedeutet. Kein Marketing „auf Militärniveau“ – nur die konkreten Standards, die für uns gelten.",
    badges: [
      { label: "TLS 1.3 bei Übertragung", short: "Verschlüsselt bei Übertragung", tooltip: "Jede Verbindung zu FreeCRM ist mit TLS 1.3 verschlüsselt – dem aktuellsten Standard für Transportverschlüsselung – sodass Daten zwischen Ihrem Browser und unseren Servern unterwegs weder gelesen noch verändert werden können." },
      { label: "AES-256 im Ruhezustand", short: "Verschlüsselt gespeichert", tooltip: "Alle Kundendaten in unseren Datenbanken und Backups werden im Ruhezustand mit AES-256 verschlüsselt – derselben symmetrischen Verschlüsselung, die in regulierten Branchen für vertrauliche Informationen genutzt wird." },
      { label: "SOC 2 Typ II", short: "Unabhängig auditiert", tooltip: "Unsere Infrastruktur läuft in nach SOC 2 Typ II auditierten Rechenzentren. Ein unabhängiger Prüfer bestätigt laufend, dass die Kontrollen für Sicherheit, Verfügbarkeit und Vertraulichkeit wirksam funktionieren." },
      { label: "DSGVO- & CCPA-konform", short: "Datenschutz standardmäßig", tooltip: "FreeCRM erfüllt DSGVO- und CCPA-Anforderungen mit unterzeichnetem AV-Vertrag, Werkzeugen für Datenexport und -löschung, regionaler Datenhaltung im Enterprise-Plan und granularen rollenbasierten Zugriffsrechten." },
    ],
  },
  videos: {
    metaTitle: "FreeCRM Video-Tutorials",
    metaDescription: "Kurze Video-Tutorials zu Einrichtung, benutzerdefinierten Feldern, Kalenderintegration und den Grundlagen von FreeCRM.",
    pageTitle: "Video-Tutorials",
    eyebrow: "Videothek",
    heading: "FreeCRM Video-Tutorials",
    subtitle: "Kurze, fokussierte Anleitungen, um FreeCRM einzurichten, Formulare anzupassen, den Kalender zu synchronisieren und Ihr Team einzuarbeiten.",
    allVideos: "Alle Videos",
    about: "Über dieses Video",
    more: "Weitere Tutorials",
    unsupported: "Ihr Browser unterstützt das Video-Tag nicht.",
    thumbnailAlt: "Video-Vorschaubild",
    secUnit: "Sek.",
    items: {
      "free-crm-user-guide": { name: "FreeCRM Benutzerhandbuch", description: "Ein Rundgang durch die FreeCRM-Oberfläche: Kontakte, Deals, Aufgaben und der Aktivitätsstream – damit neue Nutzer in Minuten produktiv sind." },
      "company-profile-and-user-setup-guide": { name: "Anleitung zu Firmenprofil und Benutzereinrichtung", description: "So richten Sie Ihr FreeCRM-Firmenprofil ein, laden Kolleginnen und Kollegen ein und vergeben Rollen, damit Ihr Vertriebsteam sofort mit Leads arbeiten kann." },
      "adding-custom-fields-to-forms": { name: "Benutzerdefinierte Felder zu Formularen hinzufügen", description: "Schritt-für-Schritt-Anleitung, um Webformularen in FreeCRM benutzerdefinierte Felder hinzuzufügen und genau die Lead-Daten zu erfassen, die Sie brauchen." },
      "adding-and-customizing-crm-form-fields": { name: "CRM-Formularfelder hinzufügen und anpassen", description: "Erfahren Sie, wie Sie Formularfelder in FreeCRM hinzufügen, neu anordnen und anpassen – passend zu Ihren Qualifizierungskriterien und Pipeline-Phasen." },
      "android-crm-app-user-guide": { name: "Handbuch zur Android-CRM-App", description: "Ein Rundgang durch die FreeCRM-Android-App: Kontakte, Deals und Aufgaben mobil verwalten und unterwegs produktiv bleiben." },
      "managing-and-editing-contacts": { name: "Kontakte verwalten und bearbeiten", description: "So legen Sie Kontakte in FreeCRM an, aktualisieren und organisieren sie, damit Ihre Kundendaten sauber, aktuell und für das Team nutzbar bleiben." },
      "crm-calendar-integration-guide": { name: "Anleitung zur CRM-Kalenderintegration", description: "So verbinden Sie Google Kalender mit FreeCRM, damit Meetings, Follow-ups und geplante Anrufe zwischen CRM und Kalender synchron bleiben." },
    },
  },
  notFound: {
    metaTitle: "Seite nicht gefunden — FreeCRM",
    metaDescription: "Die gesuchte Seite existiert nicht. Kehren Sie zu FreeCRM zurück, um zu finden, was Sie brauchen.",
    heading: "Hoppla! Seite nicht gefunden",
    home: "Zurück zur Startseite",
  },
};

const ar: ExtraBundle = {
  nav: { videos: "الفيديوهات", support: "الدعم" },
  appBadges: {
    downloadOn: "حمّله من", appStore: "App Store", getItOn: "احصل عليه من", googlePlay: "Google Play",
    iosApp: "تطبيق iOS", androidApp: "تطبيق Android",
    appStoreAria: "حمّل FreeCRM من App Store", googlePlayAria: "احصل على FreeCRM من Google Play",
    takeWithYou: "خذ FreeCRM معك",
  },
  footer: {
    tagline: "نظام CRM مجاني ينمو مع أعمالك.",
    copyright: "جميع الحقوق محفوظة.",
    tagBuiltFor: "صُمم للفرق التي تستحق أفضل من HubSpot.",
    cols: { product: "المنتج", company: "الشركة", resources: "الموارد", legal: "الشؤون القانونية" },
    links: {
      features: "المزايا", pricing: "الأسعار", integrations: "التكاملات", industries: "القطاعات",
      about: "من نحن", customers: "العملاء", contact: "اتصل بنا", support: "الدعم",
      helpCenter: "مركز المساعدة", apiDocs: "وثائق API", privacy: "الخصوصية", terms: "الشروط",
    },
  },
  pricing: {
    eyebrow: "الأسعار",
    title: "أسعار برنامج إدارة علاقات العملاء",
    subtitle: "أرقام هاتف أمريكية بـ 1.15$ شهريًا فقط · 0.01$ للدقيقة · 0.01$ لكل رسالة SMS",
    mostPopular: "الأكثر شيوعًا",
    perUserMo: "/مستخدم/شهر",
    tiers: [
      { name: "Free", price: "$0", blurb: "مجاني للأبد — الأساسيات لإدارة جهات الاتصال والصفقات والمهام.", cta: "سجّل مجانًا" },
      { name: "Pro", price: "$10", blurb: "قوة CRM كاملة لفرق المبيعات المتنامية.", cta: "سجّل الآن" },
      { name: "Enterprise", price: "$25", blurb: "مزايا متقدمة للفرق ذات الأحجام الكبيرة.", cta: "سجّل الآن" },
    ],
    f: {
      storage: "تخزين غير محدود", contacts: "جهات الاتصال والشركات", deals: "الصفقات والمهام",
      cases: "الحالات والمكالمات", calendar: "التقويم والمستندات", reports: "التقارير",
      apps: "تطبيقات iOS وAndroid", campaigns: "الحملات والنماذج", email: "البريد الإلكتروني",
      googleSync: "مزامنة Google", quickbooks: "QuickBooks", sms: "الرسائل النصية", voice: "المكالمات الصوتية",
      creditCard: "بطاقة ائتمان", support: "دعم العملاء", noSupport: "بدون دعم", api: "الوصول إلى API",
    },
  },
  trust: {
    eyebrow: "ادعاءات موثّقة",
    title: "أمان يمكنك التحقق منه، لا مجرد صفات",
    subtitle: "مرّر المؤشر فوق أي شارة لترى معناها بدقة. لا تسويق بعبارات «بمستوى عسكري» — بل معايير محددة نلتزم بها.",
    badges: [
      { label: "TLS 1.3 أثناء النقل", short: "مشفّر أثناء النقل", tooltip: "كل اتصال بـ FreeCRM مشفّر باستخدام TLS 1.3 — أحدث معيار لأمان طبقة النقل — بحيث لا يمكن قراءة البيانات المتبادلة بين متصفحك وخوادمنا أو العبث بها أثناء النقل." },
      { label: "AES-256 أثناء التخزين", short: "مشفّر أثناء التخزين", tooltip: "تُشفَّر جميع بيانات العملاء المخزّنة في قواعد بياناتنا ونسخنا الاحتياطية باستخدام AES-256، وهو التشفير المتماثل نفسه المستخدم لحماية المعلومات السرية في القطاعات المنظّمة." },
      { label: "SOC 2 النوع الثاني", short: "مدقّق بشكل مستقل", tooltip: "تُستضاف بنيتنا التحتية في مراكز بيانات مدققة وفق SOC 2 النوع الثاني. يتحقق مدقق مستقل بشكل مستمر من فعالية ضوابط الأمان والتوافر والسرية." },
      { label: "متوافق مع GDPR وCCPA", short: "الخصوصية أولًا افتراضيًا", tooltip: "يدعم FreeCRM متطلبات GDPR وCCPA عبر اتفاقية معالجة بيانات موقّعة، وأدوات تصدير البيانات وحذفها، وإقامة إقليمية للبيانات في خطة Enterprise، وضوابط وصول دقيقة حسب الدور." },
    ],
  },
  videos: {
    metaTitle: "دروس FreeCRM بالفيديو",
    metaDescription: "دروس فيديو قصيرة حول الإعداد والحقول المخصصة وتكامل التقويم وأساسيات استخدام FreeCRM.",
    pageTitle: "دروس بالفيديو",
    eyebrow: "مكتبة الفيديو",
    heading: "دروس FreeCRM بالفيديو",
    subtitle: "شروحات قصيرة ومركّزة لإعداد FreeCRM وتخصيص النماذج ومزامنة التقويم وتدريب فريقك.",
    allVideos: "كل الفيديوهات",
    about: "عن هذا الفيديو",
    more: "المزيد من الدروس",
    unsupported: "متصفحك لا يدعم عنصر الفيديو.",
    thumbnailAlt: "صورة مصغّرة للفيديو",
    secUnit: "ثانية",
    items: {
      "free-crm-user-guide": { name: "دليل استخدام FreeCRM", description: "جولة في واجهة FreeCRM: التنقل بين جهات الاتصال والصفقات والمهام وسجل النشاط ليصبح المستخدمون الجدد منتجين خلال دقائق." },
      "company-profile-and-user-setup-guide": { name: "دليل ملف الشركة وإعداد المستخدمين", description: "كيفية إعداد ملف شركتك في FreeCRM ودعوة الزملاء وتعيين الأدوار ليبدأ فريق المبيعات العمل على العملاء المحتملين." },
      "adding-custom-fields-to-forms": { name: "إضافة حقول مخصصة إلى النماذج", description: "دليل خطوة بخطوة لإضافة حقول مخصصة إلى نماذج الويب في FreeCRM لالتقاط بيانات العملاء المحتملين التي تحتاجها عمليتك البيعية بالضبط." },
      "adding-and-customizing-crm-form-fields": { name: "إضافة حقول نماذج CRM وتخصيصها", description: "تعرّف على كيفية إضافة حقول النماذج وإعادة ترتيبها وتخصيصها في FreeCRM بما يتوافق مع معايير التأهيل ومراحل مسار المبيعات." },
      "android-crm-app-user-guide": { name: "دليل تطبيق CRM على Android", description: "جولة في تطبيق FreeCRM على Android: جهات الاتصال والصفقات والمهام على الهاتف ليبقى مندوبوك منتجين أثناء التنقل." },
      "managing-and-editing-contacts": { name: "إدارة جهات الاتصال وتحريرها", description: "كيفية إنشاء جهات الاتصال وتحديثها وتنظيمها في FreeCRM لتبقى سجلات العملاء نظيفة ومحدّثة وسهلة الاستخدام للفريق." },
      "crm-calendar-integration-guide": { name: "دليل تكامل التقويم مع CRM", description: "كيفية ربط تقويم Google بـ FreeCRM لتبقى الاجتماعات والمتابعات والمكالمات المجدولة متزامنة بين نظامك والتقويم." },
    },
  },
  notFound: {
    metaTitle: "الصفحة غير موجودة — FreeCRM",
    metaDescription: "الصفحة التي تبحث عنها غير موجودة. عد إلى FreeCRM للعثور على ما تحتاجه.",
    heading: "عذرًا! الصفحة غير موجودة",
    home: "العودة إلى الصفحة الرئيسية",
  },
};

export const extraBundles: Record<Lang, ExtraBundle> = { en, es, fr, pt, it, de, ar };

export function useExtra(): ExtraBundle {
  return extraBundles[detectLang()];
}

/** Localizes labels like "17 sec". */
export function formatDuration(label: string, unit: string) {
  const n = label.match(/\d+/)?.[0];
  return n ? `${n} ${unit}` : label;
}
